import * as React from 'react';
import BootstrapTable from 'react-bootstrap-table-next';
import overlayFactory from 'react-bootstrap-table2-overlay';
import paginationFactory from 'react-bootstrap-table2-paginator';
import DatePicker from 'react-datepicker';

import {RouteComponentProps} from 'react-router';
import {Debounce} from 'react-throttle';

import {Col, Grid, Row} from 'react-bootstrap';

import 'react-datepicker/dist/react-datepicker.css';

import * as moment from 'moment-timezone';

moment.tz.setDefault('America/Los_Angeles');

interface IEventsRouterProps {
	type: string;
}

interface IEventsDispatchProps {
	any?: any
}

interface IColumnsMap {
	dataField: string;
	text: string;
	sort?: boolean;
	formatter?: (cell, row, rowIndex, formatExtraData) => any;
}

interface IColumns {
	[key: string]: IColumnsMap[]
}

interface IEventsProps extends RouteComponentProps<IEventsRouterProps> {
	any?: any
}

const RemotePagination = ({loading, data, page, sizePerPage, sort, onTableChange, totalSize, type}) => (
	<div>
		<BootstrapTable
			remote={true}
			loading={loading}
			keyField="id"
			data={data}
			defaultSorted={sort}
			columns={eventColumnMap[type]}
			pagination={paginationFactory({page, sizePerPage, totalSize, sizePerPageList: [10, 25, 50, 100, 250, 500]})}
			onTableChange={onTableChange}
			overlay={overlayFactory({spinner: true, background: 'rgba(192,192,192,0.3)'})}
			noDataIndication="No events"
			hover={true}
		/>
	</div>
);

const tzFormatter = (cell): any => moment(cell).toISOString(true);

const factionFormatter = (cell, row): any => {
	let returnString = '';
	if (!row || !row.FactionEffects) {
		return returnString;
	}
	for (const i of row.FactionEffects) {
		if (!i.Faction.trim()) {
			continue;
		}
		returnString += `${i.Faction}:\n `;
		for (const inf of i.Influence) {
			returnString += `${inf.Trend}\n`;
		}
	}
	return (
		returnString
	)
};

const targetFactionFormatter = (cell, row): any => {
	let returnString = '';
	if (!row || !row.FactionEffects) {
		return returnString;
	}
	for (const i of row.FactionEffects) {
		if (!i.Faction.trim()) {
			continue;
		}
		returnString += `${i.Faction}, `;
	}
	return (
		returnString
	)
};

const RedeemVoucherFactionFormatter = (cell, row): any => {
	let returnString = '';
	if (!row.Factions) {
		return <span> {cell} </span>
	}
	for (const i of row.Factions) {
		returnString += `${i.Faction}: ${i.Amount}CR, `;
	}
	return (
		<span>{returnString}</span>
	)
};

const eventColumnMap: IColumns = {
	Bounty: [{dataField: 'cmdr', text: 'CMDR', sort: true}, {
		dataField: 'StarSystem',
		text: 'System',
		sort: true
	}, {dataField: 'StationName', text: 'Station', sort: true}, {
		dataField: 'TotalReward',
		text: 'Bounty CR',
		sort: true
	}, {dataField: 'VictimFaction', text: 'Victim Faction', sort: true}, {
		dataField: 'timestamp',
		text: 'Time',
		sort: true,
		formatter: tzFormatter
	}],
	Default: [{dataField: 'cmdr', text: 'CMDR', sort: true}, {
		dataField: 'message.StarSystem',
		text: 'System',
		sort: true
	}, {dataField: 'message.StationName', text: 'Station', sort: true}, {
		dataField: 'message.event',
		text: 'Event',
		sort: true
	}, {dataField: 'message.timestamp', text: 'Time', sort: true, formatter: tzFormatter}],
	FSDJump: [{dataField: 'cmdr', text: 'CMDR', sort: true}, {
		dataField: 'StarSystem',
		text: 'System',
		sort: true
	}, {dataField: 'timestamp', text: 'Time', sort: true, formatter: tzFormatter}],
	FactionKillBond: [{dataField: 'cmdr', text: 'CMDR', sort: true}, {
		dataField: 'StarSystem',
		text: 'System',
		sort: true
	}, {dataField: 'StationName', text: 'Station', sort: true}, {
		dataField: 'Reward',
		text: 'Bond CR',
		sort: true
	}, {dataField: 'VictimFaction', text: 'Faction Killed', sort: true}, {
		dataField: 'timestamp',
		text: 'Time',
		sort: true,
		formatter: tzFormatter
	}],
	MarketBuy: [{dataField: 'cmdr', text: 'CMDR', sort: true}, {
		dataField: 'StarSystem',
		text: 'System',
		sort: true
	}, {dataField: 'StationName', text: 'Station', sort: true}, {
		dataField: 'Count',
		text: 'Amount',
		sort: true
	}, {dataField: 'Type', text: 'Commodity', sort: true}, {
		dataField: 'timestamp',
		text: 'Time',
		sort: true,
		formatter: tzFormatter
	}],
	MarketSell: [{dataField: 'cmdr', text: 'CMDR', sort: true}, {
		dataField: 'StarSystem',
		text: 'System',
		sort: true
	}, {dataField: 'StationName', text: 'Station', sort: true}, {
		dataField: 'Count',
		text: 'Amount',
		sort: true
	}, {dataField: 'Type', text: 'Commodity', sort: true}, {
		dataField: 'TotalSale',
		text: 'Total Sell',
		sort: true
	}, {dataField: 'AvgPricePaid', text: 'Avg Price Paid', sort: true}, {
		dataField: 'SellPrice',
		text: 'Sell Price',
		sort: true
	}, {dataField: 'timestamp', text: 'Time', sort: true, formatter: tzFormatter}],
	MissionAbandoned: [{dataField: 'cmdr', text: 'CMDR', sort: true}, {
		dataField: 'StarSystem',
		text: 'System',
		sort: true
	}, {dataField: 'StationName', text: 'Station', sort: true}, {
		dataField: 'Name',
		text: 'Mission',
		sort: true
	}, {dataField: 'timestamp', text: 'Time', sort: true, formatter: tzFormatter}],
	MissionAccepted: [{dataField: 'cmdr', text: 'CMDR', sort: true}, {
		dataField: 'StarSystem',
		text: 'System',
		sort: true
	}, {dataField: 'StationName', text: 'Station', sort: true}, {
		dataField: 'Reward',
		text: 'Reward (CR)',
		sort: true
	}, {dataField: 'Faction', text: 'Mission Faction', sort: true}, {
		dataField: 'Influence',
		text: 'Influence',
		sort: true
	}, {dataField: 'Reputation', text: 'Reputation', sort: true}, {
		dataField: 'timestamp',
		text: 'Time',
		sort: true,
		formatter: tzFormatter
	}],
	MissionCompleted: [{dataField: 'cmdr', text: 'CMDR', sort: true}, {
		dataField: 'StarSystem',
		text: 'System',
		sort: true
	}, {dataField: 'StationName', text: 'Station', sort: true}, {
		dataField: 'Faction',
		text: 'Mission Faction',
		sort: true
	}, {dataField: 'Reward', text: 'Reward (CR)', sort: true}, {
		dataField: 'TargetFaction',
		text: 'Target Faction(s)',
		formatter: targetFactionFormatter,
		sort: true
	}, {dataField: 'Station', text: 'Station', sort: true}, {
		dataField: 'StarSystem',
		text: 'System',
		sort: true
	}, {dataField: 'FactionEffects', formatter: factionFormatter, text: 'Effect', sort: true}, {
		dataField: 'timestamp',
		text: 'Time',
		sort: true,
		formatter: tzFormatter
	}],
	MissionFailed: [{dataField: 'cmdr', text: 'CMDR', sort: true}, {
		dataField: 'StarSystem',
		text: 'System',
		sort: true
	}, {dataField: 'StationName', text: 'Station', sort: true}, {
		dataField: 'Name',
		text: 'Name',
		sort: true
	}, {dataField: 'timestamp', text: 'Time', sort: true, formatter: tzFormatter}],
	MissionRedirected: [{dataField: 'cmdr', text: 'CMDR', sort: true}, {
		dataField: 'StarSystem',
		text: 'System',
		sort: true
	}, {dataField: 'StationName', text: 'Station', sort: true}, {
		dataField: 'timestamp',
		text: 'Time',
		sort: true,
		formatter: tzFormatter
	}],
	RedeemVoucher: [{dataField: 'cmdr', text: 'CMDR', sort: true}, {
		dataField: 'StarSystem',
		text: 'System',
		sort: true
	}, {dataField: 'StationName', text: 'Station', sort: true}, {
		dataField: 'Type',
		text: 'Type',
		sort: true
	}, {dataField: 'Amount', text: 'Amount (CR)', sort: true}, {
		dataField: 'Factions',
		formatter: RedeemVoucherFactionFormatter,
		sort: true,
		text: 'CR/Faction (Bounties)'
	}, {dataField: 'Faction', text: 'Faction', sort: true}, {
		dataField: 'timestamp',
		text: 'Time',
		sort: true,
		formatter: tzFormatter
	}],
	Scan: [{dataField: 'cmdr', text: 'CMDR', sort: true}, {
		dataField: 'StarSystem',
		text: 'System',
		sort: true
	}, {dataField: 'StationName', text: 'Station', sort: true}, {
		dataField: 'BodyName',
		text: 'Name',
		sort: true
	}, {dataField: 'ScanType', text: 'Scan Type', sort: true}, {
		dataField: 'timestamp',
		text: 'Time',
		sort: true,
		formatter: tzFormatter
	}],
	SellExplorationData: [{dataField: 'cmdr', text: 'CMDR', sort: true}, {
		dataField: 'StarSystem',
		text: 'System',
		sort: true
	}, {dataField: 'StationName', text: 'Station', sort: true}, {
		dataField: 'TotalEarnings',
		text: 'Total (CR)',
		sort: true
	}, {dataField: 'Bonus', text: 'Bonus (CR)', sort: true}, {
		dataField: 'BaseValue',
		text: 'Base Value (CR)',
		sort: true
	}, {dataField: 'timestamp', text: 'Time', sort: true, formatter: tzFormatter}]
};

export default class Events extends React.Component<IEventsProps & IEventsDispatchProps> {
	public allowedEvents: string[] = Object.keys(eventColumnMap);

	public state: { system: string; events: any[]; type: string; cmdr: string; hasEvents: boolean, page: number, sizePerPage: number, totalSize: number, loading: boolean, cmdrs: string[]; username: string; password: string; sortField: string; sortOrder: string; date: moment.Moment; } = {
		cmdr: '',
		cmdrs: [],
		date: undefined,
		events: [],
		system: '',
		hasEvents: false,
		loading: false,
		page: 1,
		password: localStorage.getItem('pw') || '',
		sizePerPage: 50,
		sortField: '',
		sortOrder: '',
		totalSize: 0,
		type: 'Default',
		username: localStorage.getItem('user') || ''

	};

	constructor(props: IEventsProps & IEventsDispatchProps) {
		super(props);
		this.state.type = props.match.params.type || 'Default'
	}

	public selectChangeHandler = (e: React.ChangeEvent<any>) => {
		this.props.history.push(`/events/${e.target.value}`);
		this.setState({
			type: e.target.value
		}, async () => {
			await this.fetchData(0);
		});
	};

	public cmdrChangeHandler = (e: React.ChangeEvent<any>) => {
		this.setState({
			cmdr: e.target.value,
			loading: true
		}, async () => {
			await this.fetchData(0);
		});
	};

	public systemChangeHandler = (e: React.ChangeEvent<any>) => {
		this.setState({
			system: e.target.value,
			loading: true
		}, async () => {
			await this.fetchData(0);
		});
	};

	public dateChangeHandler = (e: Date) => {
		this.setState({
			date: e,
			loading: true
		}, async () => {
			await this.fetchData(0);
		});
	};

	public handleTableChange = async (type, {sortField, sortOrder, page, sizePerPage}) => {
		const effectiveSizePerPage = sizePerPage != null ? sizePerPage : this.state.sizePerPage;
		const effectivePage = page != null ? page : this.state.page;
		let currentIndex = (effectivePage - 1) * effectiveSizePerPage;
		if (isNaN(currentIndex)) {
			currentIndex = 0;
		}
		this.setState({
			sortField: sortField != null ? sortField : this.state.sortField,
			sortOrder: sortOrder != null ? sortOrder : this.state.sortOrder,
			page: effectivePage,
			sizePerPage: effectiveSizePerPage,
			loading: true
		}, async () => {
			await this.fetchData(currentIndex);
		});
	};

	public selectType() {
		return (
			<Grid className={'form-group'}>
				<Row>
					<Col md={6}>
						<label>CMDR Select</label>
						<select id={'CMDRDropdown'} className={'form-control'} value={this.state.cmdr} onChange={this.cmdrChangeHandler}>
							<option value=''>Select a CMDR</option>
							{this.state.cmdrs.map(e => <option key={e} value={e}>{e}</option>)}
						</select>
					</Col>
					<Col md={6}>
						<label>Date</label>
						<DatePicker selected={this.state.date} todayButton={'Today'} onChange={this.dateChangeHandler}
									placeholderText="Click to select a date"/>
					</Col>
				</Row>
				<Row>
					<Col md={6}>
						<label>Custom System</label>
						<Debounce time="400" handler="onChange">
							<input id={'SystemSelect'} placeholder={'System Name'} className={'form-control'} type={'text'}
								   onChange={this.systemChangeHandler}/>
						</Debounce>
					</Col>
					<Col md={6}>
						<label>Event</label>
						<Debounce time="400" handler="onChange">
							<select defaultValue={this.state.type} className={'form-control'}
									onChange={this.selectChangeHandler}>
								<option>Default</option>
								{this.allowedEvents.map(e => <option key={e}>{e}</option>)}
							</select>
						</Debounce>
					</Col>
				</Row>
			</Grid>
		)
	}

	public getColumns() {
		const list = [];
		for (let i = 0; i <= this.state.events.length; i++) {
			if (!this.state.events[i] || !this.state.events[i].message) {
				continue;
			}
			for (const key in this.state.events[i].message || {}) {
				if (list.indexOf({
					dataField: `message.${key}`,
					text: key
				}) === -1 && typeof this.state.events[i].message[key] !== 'object') {
					list.push({dataField: `message.${key}`, text: key});
				}
			}
		}
		return list
	}

	public getCmdrs() {
		const form = new FormData();
		form.append('username', this.state.username);
		form.append('password', this.state.password);
		return fetch(`/api/cmdrs`, {
			body: form,
			credentials: 'include',
			method: 'POST'
		})
			.then(res => res.json())
			.then(data => {
				this.setState({cmdrs: data});
			})
			.catch(err => {
				console.error(err);
			})
	}

	public getSort() {
		return [{dataField: this.state.type === 'Default' ? 'message.timestamp' : 'timestamp', order: 'desc'}]
	}

	public render() {
		const sort = this.getSort.bind(this);
		return (
			<Grid className={''}>
				<h1>Event</h1>
				{this.selectType()}
				{this.state.events && this.state.events.length >= 0 ?
					<RemotePagination
						loading={this.state.loading}
						data={this.state.events}
						page={this.state.page}
						sizePerPage={this.state.sizePerPage}
						sort={sort()}
						onTableChange={this.handleTableChange}
						totalSize={this.state.totalSize}
						type={this.state.type}
					/>
					: <div>Loading</div>}
			</Grid>
		)
	}

	public fetchData = (skip: number) => {
		const form = new FormData();
		form.append('username', this.state.username);
		form.append('password', this.state.password);
		return fetch(`/api/events?type=${this.state.type}&cmdr=${this.state.cmdr}&sortField=${this.state.sortField}&sortOrder=${this.state.sortOrder}&date=${this.state.date ? this.state.date.toISOString() : ''}&skip=${skip}&system=${this.state.system}&limit=${this.state.sizePerPage}`, {
			body: form,
			credentials: 'include',
			method: 'POST'
		})
			.then((res) => res.json())
			.then(events => {
				return this.setState({events: events.docs, totalSize: events.total, hasEvents: true, loading: false});
			})
			.catch((err) => {
				throw err;
			});
	};

	public async componentDidMount() {
		if (!this.state.type) {
			this.setState({hasEvents: false});
			this.selectType();
			return
		}
		await Promise.all([this.fetchData(0), this.getCmdrs()]);
	}
};



// WEBPACK FOOTER //
// ./src/Events.tsx